import React, { useState, MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage, Language } from '../../../../context/LanguageContext';

const LanguageMenuItem = styled(MenuItem)({
  padding: '10px 16px',
  minWidth: 180,
  '&:hover': {
    backgroundColor: 'rgba(85, 110, 230, 0.08)',
  },
});

const languages: Record<Language, { name: string; flag: string }> = {
  fr: { name: 'Français', flag: '🇫🇷' },
  en: { name: 'English', flag: '🇬🇧' },
  es: { name: 'Español', flag: '🇪🇸' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  lb: { name: 'Lëtzebuergesch', flag: '🇱🇺' },
  nl: { name: 'Nederlands', flag: '🇧🇪' },
};

const LanguageSelector: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang: Language) => {
    if (lang === language) return;
    setLanguage(lang);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('header.selectLanguage')}>
        <IconButton
          onClick={handleClick}
          sx={{
            color: '#495057',
            backgroundColor: open ? 'rgba(85, 110, 230, 0.1)' : 'transparent',
            minWidth: 60,
            transition: 'all 0.2s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>
              {languages[language].flag}
            </span>
            <i 
              className="bx bx-chevron-down" 
              style={{ 
                fontSize: 16,
                transition: 'transform 0.2s ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
              }} 
            />
          </Box>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: 2,
          },
        }}
      >
        {(Object.keys(languages) as Language[]).map((lang) => (
          <LanguageMenuItem
            key={lang}
            onClick={() => handleLanguageChange(lang)}
            selected={language === lang}
            sx={{
              backgroundColor: language === lang ? 'rgba(85, 110, 230, 0.1)' : 'transparent',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>
                {languages[lang].flag}
              </span>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: language === lang ? 600 : 400,
                  color: language === lang ? '#556ee6' : '#495057',
                }}
              >
                {languages[lang].name}
              </Typography>
            </Box>
          </LanguageMenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
